import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Shapes,
  Tag,
  Sparkles,
  Building2,
  Code2,
  BriefcaseBusiness,
  NotebookPen,
  GraduationCap,
  Share2,
  Twitter,
  Linkedin,
  Facebook,
  Link as LinkIcon,
  Check,
  Award,
  Eye,
  Heart,
  Image as ImageIcon
} from 'lucide-react';

const iconForCategory = {
  'All Blogs': Sparkles,
  'College Guide': Building2,
  Coding: Code2,
  Placements: BriefcaseBusiness,
  Exams: NotebookPen,
  'Student Life': GraduationCap
};

const SHARE_URL = window.location.origin + '/blogs';
const SHARE_TEXT = 'Explore student blogs, college guides & career tips on Race Next Step!';

const shareLinks = [
  {
    key: 'twitter',
    label: 'Twitter / X',
    Icon: Twitter,
    color: 'bg-[#e7f3ff] text-[#1d9bf0] hover:bg-[#1d9bf0] hover:text-white',
    href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(SHARE_URL)}&text=${encodeURIComponent(SHARE_TEXT)}`
  },
  {
    key: 'linkedin',
    label: 'LinkedIn',
    Icon: Linkedin,
    color: 'bg-[#e8f0fb] text-[#0a66c2] hover:bg-[#0a66c2] hover:text-white',
    href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(SHARE_URL)}`
  },
  {
    key: 'facebook',
    label: 'Facebook',
    Icon: Facebook,
    color: 'bg-[#edf2ff] text-[#1877f2] hover:bg-[#1877f2] hover:text-white',
    href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(SHARE_URL)}`
  }
];

const EditorPickSection = ({ blog }) => {
  const [hasImgError, setHasImgError] = useState(false);
  if (!blog) return null;

  return (
    <section className="rounded-[28px] border border-gray-100 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center gap-3 text-[#2a1f38]">
        <Award className="h-5 w-5 text-[#f36f21]" />
        <h3 className="text-xl font-bold">Editor's Pick</h3>
      </div>

      <Link to={`/blogs/${blog.slug}`} className="group block">
        <div className="relative mb-4 h-36 overflow-hidden rounded-2xl bg-[#f3effa]">
          {!hasImgError && blog.image ? (
            <img
              src={blog.image}
              alt={blog.title}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
              referrerPolicy="no-referrer"
              loading="lazy"
              onError={() => setHasImgError(true)}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#ede4fb] to-[#f8e8df] text-[#9b55dd]">
              <ImageIcon className="h-8 w-8" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#120d1d]/30 via-transparent to-transparent" />
          <span className="absolute left-3 top-3 rounded-full bg-gradient-to-r from-[#f36f21] to-[#9a51a0] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-white shadow">
            {blog.category || 'Featured'}
          </span>
        </div>
        <h4 className="line-clamp-2 text-[0.95rem] font-bold leading-snug text-[#1f1730] transition-colors group-hover:text-[#9a51a0]">
          {blog.title}
        </h4>
      </Link>

      <p className="mt-2 line-clamp-2 text-[0.82rem] leading-5 text-[#766a86]">
        {blog.description || blog.excerpt}
      </p>

      <div className="mt-3 flex items-center gap-3 text-[0.76rem] font-semibold">
        <span className="inline-flex items-center gap-1 text-[#db4f89]">
          <Heart className="h-3.5 w-3.5" />{blog.likes}
        </span>
        <span className="inline-flex items-center gap-1 text-[#4b79d9]">
          <Eye className="h-3.5 w-3.5" />{blog.views}
        </span>
        <span className="ml-auto text-[#9b8aaa]">{blog.readTime} read</span>
      </div>

      <Link
        to={`/blogs/${blog.slug}`}
        className="mt-4 flex w-full items-center justify-center rounded-2xl bg-gradient-to-r from-[#9a51a0]/10 to-[#f36f21]/10 px-4 py-2.5 text-sm font-semibold text-[#7f3df1] transition-all hover:from-[#9a51a0]/20 hover:to-[#f36f21]/20"
      >
        Read Article
      </Link>
    </section>
  );
};

const ShareSection = () => {
  const [copied, setCopied] = useState(false);

  const handleNativeShare = () => {
    if (navigator.share) {
      navigator.share({ title: 'Race Next Step Blogs', text: SHARE_TEXT, url: SHARE_URL });
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(SHARE_URL).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <section className="rounded-[28px] border border-gray-100 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center gap-3 text-[#2a1f38]">
        <Share2 className="h-5 w-5 text-[#a43ddd]" />
        <h3 className="text-xl font-bold">Share This Page</h3>
      </div>
      <p className="mb-5 text-sm leading-6 text-[#685d74]">
        Found something useful? Share it with your friends!
      </p>

      <div className="space-y-3">
        {shareLinks.map(({ key, label, Icon, color, href }) => (
          <a
            key={key}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-sm font-semibold transition-all duration-200 ${color}`}
          >
            <Icon className="h-4 w-4 shrink-0" />
            {label}
          </a>
        ))}

        {'share' in navigator && (
          <button
            type="button"
            onClick={handleNativeShare}
            className="flex w-full items-center gap-3 rounded-2xl bg-gradient-to-r from-[#9a51a0]/10 to-[#f36f21]/10 px-4 py-3 text-sm font-semibold text-[#7f3df1] transition-all duration-200 hover:from-[#9a51a0]/20 hover:to-[#f36f21]/20"
          >
            <Share2 className="h-4 w-4 shrink-0" />
            More Options
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={handleCopyLink}
        className={`mt-4 flex w-full items-center justify-center gap-2 rounded-2xl border px-4 py-3 text-sm font-semibold transition-all duration-200 ${
          copied
            ? 'border-green-200 bg-green-50 text-green-600'
            : 'border-[#eadcf8] bg-gray-50 text-[#685d74] hover:border-[#b86ae6] hover:text-[#7f3df1]'
        }`}
      >
        {copied ? (
          <><Check className="h-4 w-4" /> Link Copied!</>
        ) : (
          <><LinkIcon className="h-4 w-4" /> Copy Link</>
        )}
      </button>
    </section>
  );
};

const BlogSidebar = ({
  categories = [],
  trendingTags = [],
  selectedCategory = 'all',
  selectedTag = '',
  onCategorySelect,
  onTagSelect,
  editorPick = null
}) => {
  return (
    <aside className="space-y-6">
      <section className="rounded-[28px] border border-gray-100 bg-white p-6 shadow-sm">
        <div className="mb-5 flex items-center gap-3 text-[#2a1f38]">
          <Shapes className="h-5 w-5 text-[#a43ddd]" />
          <h3 className="text-xl font-bold">Categories</h3>
        </div>
        <div className="space-y-3">
          {categories.map((category) => {
            const Icon = iconForCategory[category.label] || Sparkles;
            const isActive = selectedCategory === category.key;

            return (
              <button
                key={category.label}
                type="button"
                onClick={() => onCategorySelect?.(category.key)}
                className={`flex w-full items-center justify-between rounded-2xl px-4 py-3 text-sm font-semibold transition-all ${
                  isActive
                    ? 'bg-gradient-to-r from-[#8a43e3] to-[#ff7d38] text-white shadow-lg'
                    : 'bg-gray-50 text-[#47385a] hover:bg-gray-100'
                }`}
              >
                <span className="flex items-center gap-3">
                  <Icon className="h-4 w-4" />
                  {category.label}
                </span>
                <span
                  className={`rounded-full px-2.5 py-1 text-xs ${
                    isActive ? 'bg-white/25 text-white' : 'bg-[#f4eefc] text-[#8c7a9d]'
                  }`}
                >
                  {String(category.count).padStart(2, '0')}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="rounded-[28px] border border-gray-100 bg-white p-6 shadow-sm">
        <div className="mb-5 flex items-center gap-3 text-[#2a1f38]">
          <Tag className="h-5 w-5 text-[#a43ddd]" />
          <h3 className="text-xl font-bold">Trending Tags</h3>
        </div>
        <div className="flex flex-wrap gap-3">
          {trendingTags.map((tagItem) => {
            const tag = typeof tagItem === 'string' ? tagItem : tagItem.tag;
            const isActive = selectedTag.toLowerCase() === String(tag || '').toLowerCase();

            return (
              <button
                key={tag}
                type="button"
                onClick={() => onTagSelect?.(tag)}
                className="rounded-full bg-[#f4ecff] px-3.5 py-2 text-sm font-semibold text-[#9b55dd] transition-colors hover:bg-[#eadcff]"
                style={isActive ? { background: '#eadcff' } : undefined}
              >
                {tag}
              </button>
            );
          })}
        </div>
      </section>

      <EditorPickSection blog={editorPick} />
    </aside>
  );
};

export default BlogSidebar;
